import * as PIXI from "pixi.js";

class WebGLSparkles {
  constructor(app) {
    this.app = app;
    this.container = new PIXI.Container();
    this.sparkles = [];
    this.time = 0;
    this.init();
  }

  drawStar(graphics, x, y, points, outerRadius, innerRadius) {
    const angleStep = (Math.PI * 2) / points;
    const halfAngleStep = angleStep / 2;

    graphics.moveTo(x + outerRadius, y);

    for (let i = 1; i <= points; i++) {
      const angle = i * angleStep;
      const halfAngle = angle - halfAngleStep;

      // Inner point
      const innerX = x + Math.cos(halfAngle) * innerRadius;
      const innerY = y + Math.sin(halfAngle) * innerRadius;
      graphics.lineTo(innerX, innerY);

      // Outer point
      const outerX = x + Math.cos(angle) * outerRadius;
      const outerY = y + Math.sin(angle) * outerRadius;
      graphics.lineTo(outerX, outerY);
    }

    graphics.closePath();
  }

  init() {
    const sparkleCount = 20;

    for (let i = 0; i < sparkleCount; i++) {
      // Create sparkle as a multi-pointed star
      const sparkle = new PIXI.Graphics();

      // Draw a 6-pointed star (like a real star/asterisk)
      this.drawStar(sparkle, 0, 0, 6, 8, 4);
      sparkle.fill(0xffffff);

      // Random starting position
      sparkle.x = Math.random() * this.app.screen.width;
      sparkle.y = Math.random() * this.app.screen.height;

      // Movement properties
      sparkle.vx = (Math.random() - 0.5) * 0.5;
      sparkle.vy = (Math.random() - 0.5) * 0.5 - 0.2; // Slight upward drift

      // Star animation properties
      sparkle.baseScale = Math.random() * 0.4 + 0.2;
      sparkle.growShrinkSpeed = Math.random() * 0.03 + 0.02;
      sparkle.growShrinkPhase = Math.random() * Math.PI * 2;
      sparkle.rotationSpeed = (Math.random() - 0.5) * 0.05; // Slower rotation for stars
      sparkle.twinkleSpeed = Math.random() * 0.04 + 0.02;
      sparkle.twinklePhase = Math.random() * Math.PI * 2;
      sparkle.baseOpacity = Math.random() * 0.6 + 0.4;
      sparkle.life = 1.0;
      sparkle.decay = Math.random() * 0.001 + 0.0005; // Slower decay
      sparkle.minScale = 0.3;
      sparkle.maxScale = 1.2;

      // Color variation (white to light yellow/blue)
      const colors = [0xffffff, 0xffffaa, 0xaaffff, 0xffaaff];
      sparkle.tint = colors[Math.floor(Math.random() * colors.length)];

      this.sparkles.push(sparkle);
      this.container.addChild(sparkle);
    }

    this.updateBound = () => this.update();
    this.app.ticker.add(this.updateBound);
  }

  update() {
    this.time += 0.016; // Roughly 60fps

    this.sparkles.forEach((sparkle, index) => {
      // Update position
      sparkle.x += sparkle.vx;
      sparkle.y += sparkle.vy;

      // Gentle floating motion
      sparkle.x += Math.cos(this.time * 0.5 + index) * 0.2;
      sparkle.y += Math.sin(this.time * 0.3 + index) * 0.1;

      // Wrap around screen
      if (sparkle.x < -20) sparkle.x = this.app.screen.width + 20;
      if (sparkle.x > this.app.screen.width + 20) sparkle.x = -20;
      if (sparkle.y < -20) sparkle.y = this.app.screen.height + 20;
      if (sparkle.y > this.app.screen.height + 20) sparkle.y = -20;

      // Update animation phases
      sparkle.growShrinkPhase += sparkle.growShrinkSpeed;
      sparkle.twinklePhase += sparkle.twinkleSpeed;

      // Growing and shrinking animation
      const growShrink = Math.sin(sparkle.growShrinkPhase) * 0.5 + 0.5; // 0 to 1
      const currentScale =
        sparkle.minScale + (sparkle.maxScale - sparkle.minScale) * growShrink;
      sparkle.scale.set(sparkle.baseScale * currentScale);

      // Rotation animation
      sparkle.rotation += sparkle.rotationSpeed;

      // Twinkling opacity animation
      const twinkle = Math.sin(sparkle.twinklePhase) * 0.4 + 0.6; // 0.2 to 1.0

      // Life decay and respawn
      sparkle.life -= sparkle.decay;
      if (sparkle.life <= 0) {
        sparkle.life = 1.0;
        sparkle.x = Math.random() * this.app.screen.width;
        sparkle.y = Math.random() * this.app.screen.height;
        sparkle.growShrinkPhase = Math.random() * Math.PI * 2;
        sparkle.twinklePhase = Math.random() * Math.PI * 2;
        sparkle.rotation = Math.random() * Math.PI * 2;
      }

      // Final opacity combining life, base opacity, and twinkling
      sparkle.alpha = sparkle.baseOpacity * sparkle.life * twinkle;
    });
  }

  destroy() {
    try {
      // Remove ticker callback
      if (this.updateBound && this.app && this.app.ticker) {
        this.app.ticker.remove(this.updateBound);
        this.updateBound = null;
      }

      // Clean up particles array
      this.sparkles = [];

      // Destroy container and all children
      if (this.container) {
        this.container.destroy({ children: true, texture: true });
        this.container = null;
      }
    } catch (e) {
      console.warn("Error destroying WebGLSparkles:", e);
    }
  }
}

export default WebGLSparkles;
